/**
 * Seal Subscriptions customizations — Second Shower widget (v2 redesign).
 *
 * Enhances Seal's real DOM (it renders client-side): injects the per-type
 * benefit checklist, injects the one-time "what you give up" list, moves the
 * Save badge into the price cluster, tidies money + the frequency label, and
 * lets FILTER plans use Seal's own per-interval description while DEVICE plans
 * use a fixed cadence line. All visual styling lives in ss-subscriptions.css.
 *
 * Plan type is derived from the visible label + product URL — no Seal IDs to
 * maintain (Seal regenerates them whenever a rule changes).
 */

/* ──────────────────────────────────────────────────────────────────────── *
 * Content, keyed by stable semantic type
 * ──────────────────────────────────────────────────────────────────────── */

const PLAN_CONTENT = {
  handBundle: {
    items: ['One filter included', 'Free infuser sampler', 'Free lifetime warranty', 'Free shipping', 'Cancel anytime'],
    planName: 'First refill in 1 month, then every 3 months', // Seal plan name — shown quiet below the icons (Seal doesn't render it)
    useSealDescription: true,
  },
  headBundle: {
    items: ['One filter included', 'Free lifetime warranty', 'Free shipping', 'Cancel anytime'],
    planName: 'First refill in 2 months, then every 4 months',
    useSealDescription: true,
  },
  handFilterOnly: {
    items: ['Free lifetime warranty', 'Free shipping', 'Cancel anytime'],
    planName: null, // filters carry the cadence in their frequency selector
    useSealDescription: true,
    packLine: '3 filters, $9 each', // 3-pack @ $27 → per-filter value, shown under the price
  },
  headFilterOnly: {
    items: ['Free lifetime warranty', 'Free shipping', 'Cancel anytime'],
    planName: null,
    useSealDescription: true,
    packLine: '2 filters, $18 each', // 2-pack @ $36 → per-filter value, shown under the price
  },
};

/* Perks the shopper gives up by choosing one-time. Shown only when active. */
const ONE_TIME_MISSING = ['Free lifetime warranty', 'Free filter shipping', 'Automatic filter refills'];

/* Button corner radius. The theme's --rounded-button is 0 (button_border_radius
 * setting = 0), but the bridge hardcodes .btn / payment buttons to 8px, so the
 * consistent radius is 8px, not the variable. Applied inline to beat Seal. */
const BTN_RADIUS = '8px';

/* ──────────────────────────────────────────────────────────────────────── *
 * Plan type detection (label + URL)
 * ──────────────────────────────────────────────────────────────────────── */

function getContainerLabel(container) {
  const labelEl =
    container.querySelector('label.sls-selling-plan-group-name') ||
    container.querySelector('.sls-option-title') ||
    container.querySelector('label');
  return ((labelEl && labelEl.textContent) || container.textContent || '').toLowerCase().trim();
}

/* The product this widget sells: the handle on the variant picker of the same product-info block
 * (the PDP, a featured-product section on the landing pages, a quick buy sheet). Not a product
 * link: complementary products in the same block link to other products. Single-variant
 * products have no picker; there the URL is the product page. */
function productPath(container) {
  const info = container.closest('.product-info');
  const picker = info && Array.prototype.find.call(info.querySelectorAll('variant-picker[handle]'),
    (el) => el.closest('.product-info') === info);
  const handle = picker && picker.getAttribute('handle');
  if (handle) return '/products/' + handle.toLowerCase();
  return (window.location.pathname || '').toLowerCase();
}

function detectPlanType(container) {
  const groupId = container.getAttribute('data-selling-plan-group-id');
  if (!groupId || !groupId.trim()) return null; // one-time / non-subscription

  const label = getContainerLabel(container);
  const text = (container.textContent || '').toLowerCase();
  const path = productPath(container);

  const isFilterPath = /\/products\/[^/]*filter/i.test(path);
  const isHandDevicePath = /shower-?hand|handheld/i.test(path) && !isFilterPath;
  const isHeadDevicePath = /shower-?head/i.test(path) && !isFilterPath;

  /* Device bundle: on a device PDP, or the rule name pairs device + filter. */
  const isBundle =
    isHandDevicePath ||
    isHeadDevicePath ||
    /subscribe\s+filters/i.test(label) ||
    /(head|hand)\s*[+&]\s*filter\s+subscription/i.test(label);

  if (isBundle) {
    if (isHandDevicePath || /\bhand\b/i.test(label)) return 'handBundle';
    if (isHeadDevicePath || /\bhead\b/i.test(label)) return 'headBundle';
    return null;
  }

  /* Filter-only plans: their label is just "Subscribe & Save" and the URL
   * carries no hand/head, so key off pack size (Hand = 3-pack, Head = 2-pack),
   * which Seal always prints in the option text. Fall back to hand/head words. */
  if (isFilterPath || /\bfilter/i.test(text)) {
    if (/\b3\s*-?\s*pack\b/i.test(text) || /\bhand\b/i.test(text)) return 'handFilterOnly';
    if (/\b2\s*-?\s*pack\b/i.test(text) || /\bhead\b/i.test(text)) return 'headFilterOnly';
  }
  return null;
}

function isOneTimeContainer(container) {
  if (container.getAttribute('data-selling-plan-group-id')) return false;
  return (
    !!container.querySelector('input[data-type="one_time"]') ||
    !!container.querySelector('[data-one-time-option-radio]') ||
    /one[\s-]*time/i.test(getContainerLabel(container))
  );
}

/* ──────────────────────────────────────────────────────────────────────── *
 * Builders
 * ──────────────────────────────────────────────────────────────────────── */

/* Benefit icons — Lucide line set (droplet / shield-check / truck / lock-open)
 * plus a matched diffuser for the aroma infuser. Keyed off the label keyword so
 * it survives copy tweaks; stroke follows the chip's copper via currentColor. */
const BENEFIT_ICONS = {
  filter: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 22a7 7 0 0 0 7-7c0-2-1-3.9-3-5.5s-3.5-4-4-6.5c-.5 2.5-2 4.9-4 6.5C8 11.1 7 13 7 15a7 7 0 0 0 7 7z"/></svg>',
  infuser: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="5" y="9.5" width="14" height="11.5" rx="5"/><path d="M9.5 7.5c1.2-1 1.2-2.8 0-3.8"/><path d="M12 6.8c1.2-1 1.2-3.2 0-4.2"/><path d="M14.5 7.5c1.2-1 1.2-2.8 0-3.8"/></svg>',
  warranty: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/><path d="m9 12 2 2 4-4"/></svg>',
  shipping: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M15 18H9"/><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.62l-3.48-4.35A1 1 0 0 0 17.52 8H14"/><circle cx="17" cy="18" r="2"/><circle cx="7" cy="18" r="2"/></svg>',
  cancel: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 9.9-1"/></svg>',
};
function benefitIcon(label) {
  const l = (label || '').toLowerCase();
  if (l.indexOf('filter') > -1) return BENEFIT_ICONS.filter;
  if (l.indexOf('infuser') > -1) return BENEFIT_ICONS.infuser;
  if (l.indexOf('warranty') > -1) return BENEFIT_ICONS.warranty;
  if (l.indexOf('shipping') > -1) return BENEFIT_ICONS.shipping;
  if (l.indexOf('cancel') > -1) return BENEFIT_ICONS.cancel;
  return BENEFIT_ICONS.warranty;
}

function createBenefits(items) {
  const wrap = document.createElement('div');
  wrap.className = 'seal-row ss-selling-plan__details__container';
  const list = items
    .map(
      (item) => `
        <div class="ss-selling-plan__checkbox__item">
          <span class="ss-benefit-ic">${benefitIcon(item)}</span>
          <span>${item}</span>
        </div>`
    )
    .join('');
  wrap.innerHTML = `<div class="ss-selling-plan__details"><div class="ss-selling-plan__checkbox">${list}</div></div>`;
  return wrap;
}

function createOneTimeMissing(items) {
  const wrap = document.createElement('div');
  wrap.className = 'ss-onetime-missing';
  wrap.innerHTML = items
    .map(
      (item) => `
        <div class="ss-onetime-missing__item">
          <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M6.5 6.5l7 7M13.5 6.5l-7 7" stroke="#A9A296" stroke-width="1.8" stroke-linecap="round"></path></svg>
          <span>${item}</span>
        </div>`
    )
    .join('');
  return wrap;
}

/* ──────────────────────────────────────────────────────────────────────── *
 * Tidy helpers (idempotent, guarded so the observer can't loop)
 * ──────────────────────────────────────────────────────────────────────── */

function reformatMoney(root) {
  root.querySelectorAll('.money').forEach((m) => {
    const next = m.textContent.replace('US$', '$').replace(/(\$\s?\d[\d,]*)\.00\b/g, '$1');
    if (m.textContent !== next) m.textContent = next;
  });
}

function reformatSavingsBadge(container) {
  const badge = container.querySelector('.sls-savings-badge');
  if (!badge) return;
  badge.childNodes.forEach((n) => {
    if (n.nodeType === 3 && /save/i.test(n.textContent)) n.textContent = n.textContent.replace(/save/i, 'Save');
  });
}

function shortenFrequencyLabel(container) {
  const label = container.querySelector('.selling-plan-group-options-name-select > label');
  if (label && label.textContent.trim() !== 'Deliver every') label.textContent = 'Deliver every';
}

/* Seal loads its widget CSS after ours and paints a repeating chevron
 * background + an extra border onto the native <select>. Inline !important
 * styles are the only reliable way to beat external, later-loaded CSS. */
function cleanSelect(container) {
  container.querySelectorAll('select.sls-select').forEach((sel) => {
    sel.style.setProperty('background', '#FAFAF8', 'important');
    sel.style.setProperty('background-image', 'none', 'important');
    sel.style.setProperty('appearance', 'none', 'important');
    sel.style.setProperty('-webkit-appearance', 'none', 'important');
    sel.style.setProperty('border', '1.5px solid #E4DED4', 'important');
    sel.style.setProperty('border-radius', BTN_RADIUS, 'important');
    sel.style.setProperty('box-shadow', 'none', 'important');
  });
  container.querySelectorAll('.sls-select-container').forEach((c) => {
    c.style.setProperty('background', 'none', 'important');
    c.style.setProperty('background-image', 'none', 'important');
    c.style.setProperty('border', 'none', 'important');
    c.style.setProperty('box-shadow', 'none', 'important');
    c.style.setProperty('padding', '0', 'important');
  });
}

/* Seal animates its radio fill (transition + background) so it slides in from
 * the left. Kill that inline so only our centered ::after dot shows. */
function cleanRadio(container) {
  container.querySelectorAll('.sls-custom-radio').forEach((r) => {
    r.style.setProperty('background', 'transparent', 'important');
    r.style.setProperty('background-image', 'none', 'important');
    r.style.setProperty('transition', 'none', 'important');
    r.style.setProperty('box-shadow', 'none', 'important');
  });
}

/* Seal's widget CSS loads AFTER ours and overrides display/flex on the option's
 * first-line, so a stylesheet rule alone can't hold the layout. Pin it with inline
 * !important (higher priority than any external sheet): a flex row with
 * align-items:baseline, so the title and the larger price sit on ONE shared
 * baseline regardless of their font sizes. The title flexes and wraps when narrow
 * (min-width:0); the price is fixed-width and pushed right (margin-left:auto), so it
 * stays top-right on the title's first line. */
function pinFirstLine(container) {
  const fl = container.querySelector('.sls-label-container > .sls-selling-plan-group-first-line');
  if (!fl) return;
  /* Seal's grid can leave the card and its wrappers content-width (wider than the
   * phone), which lets the flex row overflow and push the price off-screen. Clamp
   * the whole chain from the card up to the widget root to the viewport first. */
  const responsivePdp = document.body.classList.contains('ss-pdp');
  if (responsivePdp || window.innerWidth <= 699) {
    // The new PDP has a bounded buy column. A percentage follows that column
    // through resizing; a captured mobile pixel cap would persist on desktop.
    const cap = responsivePdp ? '100%' : Math.max(0, window.innerWidth - 28) + 'px';
    for (let p = container, i = 0; p && i < 6; p = p.parentElement, i++) {
      p.style.setProperty('max-width', cap, 'important');
      p.style.setProperty('min-width', '0', 'important');
      p.style.setProperty('box-sizing', 'border-box', 'important');
      if (p.classList && p.classList.contains('sealsubs-container')) break;
    }
  }
  /* Seal's grid can leave the label column content-width (wider than the card), so
   * nothing inside wraps. Pin the column and its row to the card width first. */
  const lc = fl.parentElement; // .sls-label-container
  /* Flex column, not block: the v7 §3 reading order (price and saving, contents, refill timing
   * and interval, then change and skip and cancel) is set with `order` in ss-subscriptions.css,
   * and `order` needs a flex parent. This is set inline because it has to beat Seal's own late
   * CSS, which is the same reason the rest of this function works inline. Column layout is
   * visually identical to the block it replaces; only the sequence changes. */
  lc.style.setProperty('display', 'flex', 'important');
  lc.style.setProperty('flex-direction', 'column', 'important');
  lc.style.setProperty('align-items', 'stretch', 'important');
  lc.style.setProperty('width', '100%', 'important');
  lc.style.setProperty('box-sizing', 'border-box', 'important');
  const row = lc.parentElement; // .seal-row
  if (row) row.style.setProperty('display', 'block', 'important');
  fl.style.setProperty('display', 'flex', 'important');
  fl.style.setProperty('flex-wrap', responsivePdp ? 'wrap' : 'nowrap', 'important');
  fl.style.setProperty('align-items', 'baseline', 'important'); // title + price share one baseline
  fl.style.setProperty('gap', '0.6rem', 'important');
  fl.style.removeProperty('position');
  const price = fl.querySelector('.sls-selling-plan-group-price, .sls-one-time-price');
  if (price) {
    price.style.setProperty('position', 'static', 'important');
    price.style.setProperty('flex', '0 1 auto', 'important'); // shrink to wrapped width when narrow, so the title keeps room
    price.style.setProperty('margin', '0 0 0 auto', 'important'); // pin right, whatever sits on the left
    price.style.removeProperty('top');
    price.style.removeProperty('right');
  }
  const name = fl.querySelector('.sls-selling-plan-group-name');
  if (name) {
    name.style.setProperty('flex', responsivePdp ? '1 0 auto' : '1 1 auto', 'important'); // fills the row; wraps when narrow
    name.style.setProperty('display', 'block', 'important');
    name.style.setProperty('min-width', '0', 'important');
    name.style.setProperty('white-space', responsivePdp ? 'nowrap' : 'normal', 'important');
    name.style.removeProperty('padding-right');
  }
}

/* ──────────────────────────────────────────────────────────────────────── *
 * Enrich
 * ──────────────────────────────────────────────────────────────────────── */

function enrichSubscription(container, planType) {
  const content = PLAN_CONTENT[planType];
  const labelContainer = container.querySelector('.sls-label-container');
  if (!content || !labelContainer) return;

  /* Devices bill on a single interval, so hide their frequency selector. */
  const isDevice = /Bundle$/.test(planType);
  container.classList.toggle('ss-plan-device', isDevice);
  container.classList.toggle('ss-plan-filter', !isDevice);

  const sealDesc = labelContainer.querySelector('.sls-selling-plan-description-content');
  /* Tidy Seal's phrasing ("$27 in every 3 months" → "every"), idempotent. */
  if (sealDesc && /\bin every\b/i.test(sealDesc.textContent)) {
    sealDesc.textContent = sealDesc.textContent.replace(/\bin every\b/gi, 'every');
  }

  if (!container.querySelector('.ss-selling-plan__details')) {
    const benefits = createBenefits(content.items);

    if (content.useSealDescription && sealDesc && sealDesc.textContent.trim()) {
      /* Description ABOVE the icons: drop the benefits in right AFTER the
       * description's top-level node in the label container (walk up to the
       * direct child, since insertBefore needs a sibling of the ref node). */
      let descTop = sealDesc;
      while (descTop && descTop.parentNode !== labelContainer) descTop = descTop.parentNode;
      if (descTop) labelContainer.insertBefore(benefits, descTop.nextSibling);
      else labelContainer.appendChild(benefits);
    } else {
      /* Fallback (no Seal description): benefits only; hide Seal's empty element. */
      labelContainer.appendChild(benefits);
      if (sealDesc && !sealDesc.textContent.trim()) sealDesc.style.display = 'none';
    }

    /* Plan name BELOW the icons (devices only — filters carry cadence in their selector). */
    if (content.planName && !labelContainer.querySelector('.ss-plan-name')) {
      const pn = document.createElement('div');
      pn.className = 'ss-plan-name';
      pn.textContent = content.planName;
      benefits.parentNode.insertBefore(pn, benefits.nextSibling);
    }
  }

  injectRefillLine(container, sealDesc, content);
  reformatSavingsBadge(container);
  shortenFrequencyLabel(container);
  softBreakTitle(container, planType);
}

/* One quiet line under the price. Its home depends on how Seal manages each region
 * of the widget, but it's always the same `.ss-refill-line`:
 *   • Device bundle → nested in the price cluster, under $79/$99. That cluster is
 *     stable (single interval) so the line survives, and it stays glued to the price
 *     when the long title wraps. Content = the live recurring cost from Seal's
 *     description ("…for $36 every 4 months…").
 *   • Filter → its own right-aligned line just AFTER the first line (in the label
 *     container). Same visual position as the bundle — directly under the price — but
 *     kept out of the price cluster, which Seal re-renders for per-interval pricing
 *     (that would wipe a nested line). Content = a fixed pack descriptor from
 *     `PLAN_CONTENT` ("2 filters, $18 each").
 * Idempotent: it finds any existing line and re-homes it rather than duplicating, so
 * it self-heals whenever Seal re-renders a region out from under it. */
function injectRefillLine(container, sealDesc, content) {
  const labelContainer = container.querySelector('.sls-label-container');
  if (!labelContainer) return;
  const firstLine = labelContainer.querySelector('.sls-selling-plan-group-first-line');
  if (!firstLine) return;

  let lineText = null;
  if (content && content.packLine) {
    lineText = content.packLine; // filter-only: pack + per-filter value
  } else {
    const text = (sealDesc && sealDesc.textContent) || '';
    const m = text.match(/for\s+(\$[\d.,]+)\s+every\s+(\d+\s+\w+)/i);
    if (m) lineText = document.body.classList.contains('ss-pdp') ? 'Refills: ' + m[1] + ' / ' + m[2] : 'then ' + m[1] + ' every ' + m[2]; // bundle: live recurring cost
  }
  if (!lineText) return;

  const priceCluster = firstLine.querySelector('.sls-selling-plan-group-price, .sls-one-time-price');
  let target = null, variant = 'after';
  if (!document.body.classList.contains('ss-pdp') && !(content && content.packLine) && priceCluster) {
    target = priceCluster; variant = 'in-price'; // bundle: nested under $79/$99 (cluster is stable)
  }
  // Filter (packLine) → variant stays 'after': its own right-aligned line directly
  // under the price, in the label container — same position as the bundle, and it
  // survives Seal's filter price-cluster re-renders.

  let line = labelContainer.querySelector('.ss-refill-line');
  if (!line) {
    line = document.createElement('div');
    line.className = 'ss-refill-line';
  }
  if (line.textContent !== lineText) line.textContent = lineText;
  line.classList.toggle('ss-refill-line--in-price', variant === 'in-price');
  line.classList.remove('ss-refill-line--freq');

  if (target) {
    if (line.parentElement !== target) target.appendChild(line);
  } else if (firstLine.nextElementSibling !== line) {
    firstLine.parentNode.insertBefore(line, firstLine.nextSibling); // just after the price row
  }
}

/* Device bundle titles ("Subscribe Filters & Save") must wrap as "Subscribe Filters"
 * / "& Save", never "…Filters &" / "Save". Glue "& Save" with a non-breaking space so
 * the only break point is before the "&". Bites only when the title actually wraps
 * (mobile); desktop stays one line. Text-node only, so the savings badge is untouched. */
function softBreakTitle(container, planType) {
  if (!/Bundle$/.test(planType)) return;
  const name = container.querySelector('.sls-selling-plan-group-name');
  if (!name) return;
  name.childNodes.forEach((n) => {
    if (n.nodeType === 3 && /&\s+Save/.test(n.textContent)) {
      const fixed = n.textContent.replace(/&\s+Save/g, '&\u00A0Save'); // \u00A0 = nbsp, only break is before the &
      if (n.textContent !== fixed) n.textContent = fixed;
    }
  });
}

function enrichOneTime(container) {
  const labelContainer = container.querySelector('.sls-label-container');
  if (!labelContainer) return;

  /* Device PDPs: rename the one-time option so it reads as device-only (no
   * filters). Seal can't do this per-product; the widget can, from the URL. */
  const nameLabel = container.querySelector('.sls-selling-plan-group-name');
  if (nameLabel) {
    const path = productPath(container);
    const isFilter = /\/products\/[^/]*filter/i.test(path);
    let label = null;
    if (!isFilter) {
      if (/shower-?hand|handheld/i.test(path)) label = 'Hand only, one-time';
      else if (/shower-?head/i.test(path)) label = 'Head only, one-time';
    }
    if (label && nameLabel.textContent.trim() !== label) nameLabel.textContent = label;
  }

  if (!labelContainer.querySelector('.ss-onetime-missing')) {
    labelContainer.appendChild(createOneTimeMissing(ONE_TIME_MISSING));
  }
}

/* ──────────────────────────────────────────────────────────────────────── *
 * Apply pass — initial + on every mutation + on Seal price updates
 * ──────────────────────────────────────────────────────────────────────── */

/* v7 §3: "Selecting one-time hides the recurring terms rather than greying them." The
 * subscription option itself stays visible and selectable, because the same contract says the
 * one-time option sits beside it rather than hidden. What collapses is the recurring detail:
 * the contents sentence, the refill timing, the refill cost, the repeat interval and the perks.
 * This function only records state in class names; ss-subscriptions.css does the hiding.
 *
 * A radio's `checked` is a property, not an attribute, so the body MutationObserver never sees
 * the change. markSelectionState is therefore also called from a change and a click listener. */
function markSelectionState(root) {
  (root || document).querySelectorAll('.sealsubs-container').forEach((wc) => {
    const containers = Array.from(wc.querySelectorAll('.sls-option-container'));
    if (!containers.length) return;
    let oneTimeSelected = false;
    containers.forEach((c) => {
      const isOneTime = isOneTimeContainer(c);
      c.classList.toggle('ss-plan-onetime', isOneTime);
      c.classList.toggle('ss-plan-sub', !isOneTime);
      const input = c.querySelector('input[type="radio"]');
      const selected = (input && input.checked) || c.classList.contains('sls-active');
      if (selected && isOneTime) oneTimeSelected = true;
    });
    wc.classList.toggle('ss-onetime-selected', oneTimeSelected);
  });
}

function applySubscriptionCustomizations(root) {
  root = root || document;
  root.querySelectorAll('.sls-option-container').forEach((container) => {
    const planType = detectPlanType(container);
    if (planType) enrichSubscription(container, planType);
    else if (isOneTimeContainer(container)) enrichOneTime(container);
    cleanRadio(container);
    cleanSelect(container);
    pinFirstLine(container);
  });
  markSelectionState(root);
  /* Reveal as soon as Seal has rendered its options — styling is CSS-driven and
   * the select is cleaned above, so this is safe even when detection misses a
   * plan. Never gate the reveal on our own enrichment, or a miss would leave the
   * widget hidden behind the FOUC gate until the 2.5s safety net. */
  document.querySelectorAll('.sealsubs-container').forEach((wc) => {
    if (wc.querySelector('.sls-option-container')) wc.classList.add('ss-ready');
  });
}

/* Coalesce bursts of mutations (Seal's render, price updates, other apps) into
 * one pass per frame — the pass is idempotent, so extra runs are wasted work. */
let ssScheduled = false;
const subscriptionsObserver = new MutationObserver(() => {
  if (ssScheduled) return;
  ssScheduled = true;
  requestAnimationFrame(() => {
    ssScheduled = false;
    applySubscriptionCustomizations(document);
  });
});
subscriptionsObserver.observe(document.body, { childList: true, subtree: true });
applySubscriptionCustomizations(document);

/* Selection is a property change, not a DOM mutation. Listen on both change and click, because
 * Seal handles clicks on its own custom radio without always firing change on the input. Both
 * land in a rAF so a click that triggers a Seal re-render is read after that render. */
['change', 'click'].forEach((evt) => {
  document.addEventListener(evt, (e) => {
    if (!e.target || !e.target.closest) return;
    if (!e.target.closest('.sls-option-container')) return;
    requestAnimationFrame(() => markSelectionState(document));
  });
});

/* Safety net: never leave the widget hidden if enrichment can't complete. */
setTimeout(function () {
  document.querySelectorAll('.sealsubs-container').forEach((w) => w.classList.add('ss-ready'));
}, 2500);

/* Money normalization runs ONLY on Seal's discrete price event plus one settle
 * pass after render — never inside the body observer. A currency-converter app
 * (the old code targeted `.money.conversion-bear-money`) keeps its own observer
 * that re-asserts `.money`; reformatting on every mutation would ping-pong with
 * it once per frame. Discrete calls settle instead of looping. */
function normalizeWidgetMoney() {
  document.querySelectorAll('.sealsubs-container').forEach((wc) => reformatMoney(wc));
}
document.addEventListener('sealsubs:price_update', () => {
  applySubscriptionCustomizations(document);
  markSelectionState(document);
  normalizeWidgetMoney();
});
setTimeout(normalizeWidgetMoney, 1200);

/* ──────────────────────────────────────────────────────────────────────── *
 * SS plan picker (snippets/ss-plan-picker.liquid, 2026-10-08, growth plan C2)
 * The plan choice on the showers, the filter refills and the ad landings is printed by the theme, not by Seal,
 * so nothing above runs on it. This element keeps these in step with the checked radio:
 *   - which card is selected (is-selected; the server prints the plan selected),
 *   - the delivery interval (filter refills): the select puts its plan id and price on the plan radio and its
 *     name on every [data-ss-plan-interval],
 *   - the saving note under Buy now (hidden unless one-time is chosen),
 *   - the price block of the same product (PDP or quick buy sheet), written only when it differs,
 *   - the choice across Impact's re-render on a colour change (the new element starts on the plan).
 * Buy now, the buy bar and the article dock repaint on 'ss:plan-change'.
 * ?purchase=onetime (EDM links, 2026-10-29) opens the page's own product on one-time; every other picker on the
 * page and every visit without it keep the plan.
 * ──────────────────────────────────────────────────────────────────────── */
(function () {
  if (!window.customElements || window.customElements.get('ss-plan-picker')) return;
  const choice = {}; // product id -> 'once' while one-time is chosen
  const interval = {}; // product id -> chosen selling plan id (filter refills)
  let onceHandle = ''; // product handle that ?purchase=onetime applies to, used up on its first picker
  try {
    const m = location.pathname.match(/\/products\/([^/]+)/);
    if (m && new URLSearchParams(location.search).get('purchase') === 'onetime') onceHandle = decodeURIComponent(m[1]);
  } catch (e) {}

  class SsPlanPicker extends HTMLElement {
    connectedCallback() {
      const select = this.querySelector('.ss-plan__interval');
      if (select && interval[this.dataset.productId] && select.value !== interval[this.dataset.productId]) select.value = interval[this.dataset.productId];
      this.applyInterval();
      let fromUrl = false;
      if (onceHandle && onceHandle === this.dataset.productHandle) {
        onceHandle = '';
        choice[this.dataset.productId] = 'once';
        fromUrl = true;
      }
      if (choice[this.dataset.productId] === 'once') {
        const once = this.querySelector('.ss-plan__opt--once .ss-plan__radio');
        if (once && !once.checked) once.checked = true;
      }
      if (!this._bound) {
        this._bound = true;
        this.addEventListener('change', (e) => {
          if (e.target.classList.contains('ss-plan__interval')) {
            // choosing an interval chooses the plan
            interval[this.dataset.productId] = e.target.value;
            this.applyInterval();
            const sub = this.querySelector('.ss-plan__opt--sub .ss-plan__radio');
            if (sub) sub.checked = true;
            choice[this.dataset.productId] = 'sub';
            this.sync(true);
            this.track();
            return;
          }
          if (!e.target.classList.contains('ss-plan__radio')) return;
          choice[this.dataset.productId] = e.target.value ? 'sub' : 'once';
          this.sync(true);
          this.track();
        });
      }
      this.sync(fromUrl);
    }

    /* The plan radio carries the interval's plan id and price; the chip names the interval. */
    applyInterval() {
      const select = this.querySelector('.ss-plan__interval');
      const sub = this.querySelector('.ss-plan__opt--sub .ss-plan__radio');
      const opt = select && select.selectedOptions[0];
      if (!opt || !sub) return;
      if (sub.value !== opt.value) sub.value = opt.value;
      if (opt.dataset.price && sub.dataset.price !== opt.dataset.price) sub.dataset.price = opt.dataset.price;
      this.querySelectorAll('[data-ss-plan-interval]').forEach((el) => { if (el.textContent !== opt.dataset.interval) el.textContent = opt.dataset.interval; });
    }

    sync(announce) {
      const checked = this.querySelector('.ss-plan__radio:checked');
      const once = !!checked && !checked.value;
      this.querySelectorAll('.ss-plan__opt').forEach((opt) => {
        const on = opt.contains(checked);
        if (opt.classList.contains('is-selected') !== on) opt.classList.toggle('is-selected', on);
      });
      const scope = this.closest('.product-info') || this.closest('product-rerender');
      const note = scope && scope.querySelector('[data-ss-plan-note="' + this.dataset.productId + '"]');
      if (note && note.hidden === once) note.hidden = !once;
      const sale = scope && scope.querySelector('.product-info__price sale-price, .quick-buy-drawer__variant sale-price');
      const price = checked && checked.dataset.price;
      if (sale && price) {
        const last = sale.lastChild;
        if (!last || last.nodeType !== 3 || last.textContent.trim() !== price) {
          Array.from(sale.childNodes).forEach((n) => { if (n.nodeType === 3) n.remove(); });
          sale.appendChild(document.createTextNode(price));
        }
      }
      if (announce) document.dispatchEvent(new CustomEvent('ss:plan-change', { detail: { productId: this.dataset.productId, plan: checked ? checked.value : '' } }));
    }

    /* GA4 ss_plan_select (analytics/events.v1.json): the visitor's own change only, never the re-sync after a
       colour change, which is why it is not on 'ss:plan-change'. */
    track() {
      const checked = this.querySelector('.ss-plan__radio:checked');
      if (!checked || typeof window.ssTrack !== 'function') return;
      const price = parseFloat(String(checked.dataset.price || '').replace(/[^\d.]/g, ''));
      window.ssTrack('ss_plan_select', {
        product: this.dataset.productHandle,
        plan: checked.value ? 'subscription' : 'onetime',
        price: isFinite(price) ? price : undefined
      });
    }
  }
  window.customElements.define('ss-plan-picker', SsPlanPicker);
  /* Impact can swap the price block after the new picker has connected; settle once the variant change is done. */
  document.addEventListener('variant:change', () => {
    setTimeout(() => document.querySelectorAll('ss-plan-picker').forEach((el) => {
      if (choice[el.dataset.productId] === 'once') {
        const once = el.querySelector('.ss-plan__opt--once .ss-plan__radio');
        if (once && !once.checked) once.checked = true;
      }
      el.sync(true);
    }), 0);
  });
})();
